import React from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCalendar, faClock, faArrowLeft, faShare, faTag } from '@fortawesome/free-solid-svg-icons';
import ReactMarkdown from 'react-markdown';
import SEO from '../SEO';
import LandingHeader from '../landing/LandingHeader';
import { getBlogPostBySlug, getRelatedPosts } from '../../data/blogPosts';
import { generateBreadcrumbJsonLd } from '../../utils/seoData';
import { useTranslation } from 'react-i18next';
import i18n from '../../i18n/config';

const BlogDetailPage: React.FC = () => {
  const { slug, lang } = useParams<{ slug: string; lang: string }>();
  const navigate = useNavigate();
  // @ts-ignore
  const { t } = useTranslation('blog');
  const i18nInstance = i18n;
  const post = slug ? getBlogPostBySlug(slug, i18nInstance) : undefined;
  const relatedPosts = post ? getRelatedPosts(post, 3, i18nInstance) : [];

  if (!post) {
    return (
      <>
        <LandingHeader />
        <div className="min-h-screen bg-gray-900 text-white flex items-center justify-center mt-[-64px]">
          <div className="text-center">
            <h1 className="text-4xl font-bold mb-4">{t('postNotFound')}</h1>
            <p className="text-gray-400 mb-8">{t('postNotFoundDescription')}</p>
            <Link
              to={`/${lang}/blog`}
              className="inline-block px-6 py-3 bg-purple-600 md:hover:bg-purple-700 rounded-lg transition-colors"
            >
              {t('backToBlog')}
            </Link>
          </div>
        </div>
      </>
    );
  }

  const formatDate = (dateString: string) => {
    const date = new Date(dateString);
    const locale = i18nInstance.language === 'en' ? 'en-US' : 'tr-TR';
    return date.toLocaleDateString(locale, {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const handleShare = async () => {
    if (navigator.share) {
      try {
        await navigator.share({
          title: post.title,
          text: post.excerpt,
          url: window.location.href,
        });
      } catch (err) {
        console.log('Share cancelled');
      }
    } else {
      // Fallback: Copy URL
      navigator.clipboard.writeText(window.location.href);
      alert(t('linkCopied'));
    }
  };

  const currentLang = lang || i18nInstance.language || 'tr';
  const breadcrumbJsonLd = generateBreadcrumbJsonLd([
    { name: currentLang === 'en' ? 'Home' : 'Ana Sayfa', url: `https://winnomi.com/${currentLang}/home` },
    { name: currentLang === 'en' ? 'Blog' : 'Blog', url: `https://winnomi.com/${currentLang}/blog` },
    { name: post.title, url: `https://winnomi.com/${currentLang}/blog/${post.slug}` },
  ]);

  const articleJsonLd = {
    '@context': 'https://schema.org',
    '@type': 'BlogPosting',
    headline: post.title,
    description: post.excerpt,
    image: `https://winnomi.com${post.image}`,
    datePublished: post.publishedAt,
    dateModified: post.updatedAt || post.publishedAt,
    author: {
      '@type': 'Person',
      name: post.author.name,
    },
    publisher: {
      '@type': 'Organization',
      name: 'Winnomi AI',
      logo: {
        '@type': 'ImageObject',
        url: 'https://winnomi.com/logo.svg',
      },
    },
    mainEntityOfPage: {
      '@type': 'WebPage',
      '@id': `https://winnomi.com/${currentLang}/blog/${post.slug}`,
    },
    url: `https://winnomi.com/${currentLang}/blog/${post.slug}`,
  };

  return (
    <>
      <SEO
        title={`${post.title} | ${t('title')}`}
        description={post.seo.metaDescription}
        keywords={post.seo.keywords.join(', ')}
        image={post.seo.ogImage || post.image}
        type="article"
        publishedTime={post.publishedAt}
        modifiedTime={post.updatedAt}
        author={post.author.name}
        jsonLd={[breadcrumbJsonLd, articleJsonLd]}
        locale={currentLang === 'en' ? 'en_US' : 'tr_TR'}
        alternateLanguages={{
          'tr': `https://winnomi.com/tr/blog/${post.slug}`,
          'en': `https://winnomi.com/en/blog/${post.slug}`,
          'x-default': `https://winnomi.com/tr/blog/${post.slug}`
        }}
      />
      <LandingHeader />
      <div className="min-h-screen bg-gray-900 text-white mt-[-64px] bg-gradient-to-br from-purple-900/20 via-gray-900 to-gray-900">
        {/* Header */}
        <div className="border-b border-white/10 pt-20">
          <div className="max-w-4xl mx-auto px-6 py-8">
            <button
              onClick={() => navigate(`/${lang}/blog`)}
              className="flex items-center gap-2 text-gray-400 md:hover:text-white mb-6 transition-colors"
            >
              <FontAwesomeIcon icon={faArrowLeft} />
              <span>{t('backToBlog')}</span>
            </button>

            <div className="mb-4">
              <span className="inline-block px-4 py-2 bg-purple-500/20 text-purple-300 rounded-full text-sm">
                {post.category}
              </span>
            </div>

            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 leading-tight">
              {post.title}
            </h1>

            <p className="text-xl text-gray-300 mb-6">
              {post.excerpt}
            </p>

            <div className="flex flex-wrap items-center gap-6 text-sm text-gray-400">
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faCalendar} />
                <span>{formatDate(post.publishedAt)}</span>
              </div>
              <div className="flex items-center gap-2">
                <FontAwesomeIcon icon={faClock} />
                <span>{post.readTime} {t('common.min')} {currentLang === 'en' ? 'read' : 'okuma'}</span>
              </div>
              <div className="flex items-center gap-2">
                <span>{post.author.name}</span>
              </div>
              <button
                onClick={handleShare}
                className="flex items-center gap-2 text-purple-400 md:hover:text-purple-300 transition-colors"
              >
                <FontAwesomeIcon icon={faShare} />
                <span>{t('share')}</span>
              </button>
            </div>
          </div>
        </div>

        {/* Content */}
        <article className="max-w-4xl mx-auto px-6 py-12">
          <div className="prose prose-invert prose-lg max-w-none">
            <div className="aspect-video rounded-2xl mb-8 overflow-hidden">
              <img
                src={post.image}
                alt={post.title}
                className="w-full h-full object-cover"
                onError={(e) => {
                  const target = e.target as HTMLImageElement;
                  target.style.display = 'none';
                  const fallback = target.parentElement;
                  if (fallback) {
                    fallback.className = 'aspect-video bg-gradient-to-br from-purple-500/20 to-pink-500/20 rounded-2xl mb-8 flex items-center justify-center';
                    fallback.innerHTML = '<span class="text-6xl">📝</span>';
                  }
                }}
              />
            </div>

            <div className="markdown-content">
              <ReactMarkdown
                components={{
                  h1: ({ children }) => <h1 className="text-3xl font-bold mt-8 mb-4">{children}</h1>,
                  h2: ({ children }) => <h2 className="text-2xl font-bold mt-6 mb-3">{children}</h2>,
                  h3: ({ children }) => <h3 className="text-xl font-semibold mt-4 mb-2">{children}</h3>,
                  p: ({ children }) => <p className="mb-4 text-gray-300 leading-relaxed">{children}</p>,
                  ul: ({ children }) => <ul className="list-disc list-inside mb-4 space-y-2 text-gray-300">{children}</ul>,
                  ol: ({ children }) => <ol className="list-decimal list-inside mb-4 space-y-2 text-gray-300">{children}</ol>,
                  li: ({ children }) => <li className="ml-4">{children}</li>,
                  code: ({ children }) => (
                    <code className="bg-white/10 px-2 py-1 rounded text-sm font-mono">{children}</code>
                  ),
                  pre: ({ children }) => (
                    <pre className="bg-white/5 p-4 rounded-lg overflow-x-auto mb-4">
                      {children}
                    </pre>
                  ),
                  blockquote: ({ children }) => (
                    <blockquote className="border-l-4 border-purple-500 pl-4 italic text-gray-400 my-4">
                      {children}
                    </blockquote>
                  ),
                  strong: ({ children }) => <strong className="font-bold text-white">{children}</strong>,
                  a: ({ href, children }) => (
                    <a
                      href={href}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="text-purple-400 md:hover:text-purple-300 underline"
                    >
                      {children}
                    </a>
                  ),
                }}
              >
                {post.content}
              </ReactMarkdown>
            </div>
          </div>

          {/* Tags */}
          {post.tags.length > 0 && (
            <div className="mt-12 pt-8 border-t border-white/10">
              <div className="flex items-center gap-2 mb-4">
                <FontAwesomeIcon icon={faTag} className="text-gray-400" />
                <span className="text-gray-400">Etiketler:</span>
              </div>
              <div className="flex flex-wrap gap-2">
                {post.tags.map((tag) => (
                  <span
                    key={tag}
                    className="px-3 py-1 bg-white/5 border border-white/10 rounded-full text-sm text-gray-300"
                  >
                    #{tag}
                  </span>
                ))}
              </div>
            </div>
          )}
        </article>

        {/* Related Posts */}
        {relatedPosts.length > 0 && (
          <section className="max-w-7xl mx-auto px-6 py-12 border-t border-white/10">
            <h2 className="text-2xl font-bold mb-6">{t('relatedPosts')}</h2>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
              {relatedPosts.map((relatedPost) => (
                <Link
                  key={relatedPost.id}
                  to={`/${lang}/blog/${relatedPost.slug}`}
                  className="group bg-white/5 border border-white/10 rounded-2xl overflow-hidden md:hover:border-purple-500/50 transition-all"
                >
                  <div className="aspect-video relative overflow-hidden">
                    <img
                      src={relatedPost.image}
                      alt={relatedPost.title}
                      className="w-full h-full object-cover"
                      onError={(e) => {
                        const target = e.target as HTMLImageElement;
                        target.style.display = 'none';
                        const fallback = target.parentElement;
                        if (fallback) {
                          fallback.className = 'aspect-video bg-gradient-to-br from-purple-500/20 to-pink-500/20 flex items-center justify-center';
                          fallback.innerHTML = '<span class="text-4xl">📝</span>';
                        }
                      }}
                    />
                  </div>
                  <div className="p-6">
                    <span className="inline-block px-3 py-1 bg-purple-500/20 text-purple-300 rounded-full text-sm mb-3">
                      {relatedPost.category}
                    </span>
                    <h3 className="text-lg font-semibold mb-2 md:group-hover:text-purple-400 transition-colors line-clamp-2">
                      {relatedPost.title}
                    </h3>
                    <p className="text-gray-400 text-sm line-clamp-2">
                      {relatedPost.excerpt}
                    </p>
                  </div>
                </Link>
              ))}
            </div>
          </section>
        )}

        {/* CTA Section */}
        <section className="max-w-4xl mx-auto px-6 py-12">
          <div className="bg-gradient-to-r from-purple-600/20 to-pink-600/20 border border-purple-500/30 rounded-2xl p-8 text-center">
            <h2 className="text-2xl font-bold mb-4">{t('cta.title')}</h2>
            <p className="text-gray-300 mb-6">
              {t('cta.description')}
            </p>
            <Link
              to={`/${lang}/register`}
              className="inline-block px-6 py-3 bg-purple-600 md:hover:bg-purple-700 rounded-lg transition-colors font-semibold"
            >
              {t('cta.button')}
            </Link>
          </div>
        </section>
      </div>
    </>
  );
};

export default BlogDetailPage;

